import React, { useState, useEffect } from 'react';
import { Bell, Settings, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';

const NotificationSettings = () => {
  const [preferences, setPreferences] = useState({
    task_overdue: true,
    task_assigned: true,
    lead_assigned: true,
    workflow_triggered: true,
    email_notifications: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences || preferences);
      } else {
        throw new Error('Failed to fetch preferences');
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notification preferences',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const updatedPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };

    setPreferences(updatedPreferences);

    try {
      setSaving(true);
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ [key]: updatedPreferences[key] })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Notification preferences updated'
        });
      } else {
        throw new Error('Failed to update preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      // Revert on error
      setPreferences(preferences);
      toast({
        title: 'Error',
        description: 'Failed to update preferences',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-gray-700" />
              <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Manage which notifications you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Task Notifications */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Task Notifications
              </h3>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex-1">
                  <Label htmlFor="task_overdue" className="text-base font-medium">
                    Overdue Tasks
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Get notified when tasks become overdue
                  </p>
                </div>
                <Switch
                  id="task_overdue"
                  checked={preferences.task_overdue}
                  onCheckedChange={() => handleToggle('task_overdue')}
                  disabled={saving}
                />
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex-1">
                  <Label htmlFor="task_assigned" className="text-base font-medium">
                    Task Assignments
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Get notified when a task is assigned to you
                  </p>
                </div>
                <Switch
                  id="task_assigned"
                  checked={preferences.task_assigned}
                  onCheckedChange={() => handleToggle('task_assigned')}
                  disabled={saving}
                />
              </div>
            </div>

            {/* Lead Notifications */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Lead Notifications
              </h3>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex-1">
                  <Label htmlFor="lead_assigned" className="text-base font-medium">
                    Lead Assignments
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Get notified when a lead is assigned to you
                  </p>
                </div>
                <Switch
                  id="lead_assigned"
                  checked={preferences.lead_assigned}
                  onCheckedChange={() => handleToggle('lead_assigned')}
                  disabled={saving}
                />
              </div>
            </div>

            {/* Workflow Notifications */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Workflow Notifications
              </h3>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex-1">
                  <Label htmlFor="workflow_triggered" className="text-base font-medium">
                    Workflow Triggers
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Get notified when workflows are triggered on your leads
                  </p>
                </div>
                <Switch
                  id="workflow_triggered"
                  checked={preferences.workflow_triggered}
                  onCheckedChange={() => handleToggle('workflow_triggered')}
                  disabled={saving}
                />
              </div>
            </div>

            {/* Email Notifications */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Delivery Method
              </h3>

              <div className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <Label htmlFor="email_notifications" className="text-base font-medium">
                    Email Notifications
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Receive notification emails (in addition to in-app notifications)
                  </p>
                </div>
                <Switch
                  id="email_notifications"
                  checked={preferences.email_notifications}
                  onCheckedChange={() => handleToggle('email_notifications')}
                  disabled={saving}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> In-app notifications will always be shown regardless of your
            preferences. These settings control which types of events trigger notifications.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
